type StudyMode = 'flashcards' | 'speedMatch'

export interface FlashcardsSessionSnapshot {
  mode: 'flashcards'
  deckId: string
  classId: string | null
  startedAt: string
  currentIndex: number
  flipped: boolean
  missedCardIds: string[]
  cardsSeen: string[]
  cardsCorrect: string[]
}

export interface SpeedMatchSessionSnapshot {
  mode: 'speedMatch'
  deckId: string
  classId: string | null
  startedAt: string
  selectedTermId: string | null
  matches: Record<string, string>
  incorrectAttempts: Record<string, number>
  elapsedSeconds: number
  paused: boolean
  termOrder: string[]
  definitionOrder: string[]
}

export type StudySessionSnapshot = FlashcardsSessionSnapshot | SpeedMatchSessionSnapshot

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function storageKey(uid: string, deckId: string, mode: StudyMode) {
  return `study-decks.session.${uid}.${deckId}.${mode}`
}

function getStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

function hasValidEnvelope(value: unknown): value is StudySessionSnapshot {
  if (typeof value !== 'object' || value === null) return false
  const snapshot = value as Partial<StudySessionSnapshot>
  return (
    (snapshot.mode === 'flashcards' || snapshot.mode === 'speedMatch') &&
    typeof snapshot.deckId === 'string' &&
    typeof snapshot.startedAt === 'string'
  )
}

export function saveSessionSnapshot(
  uid: string,
  snapshot: StudySessionSnapshot,
  storage = getStorage(),
): void {
  if (!storage) return
  storage.setItem(storageKey(uid, snapshot.deckId, snapshot.mode), JSON.stringify(snapshot))
}

export function loadSessionSnapshot(
  uid: string,
  deckId: string,
  mode: StudyMode,
  storage = getStorage(),
): StudySessionSnapshot | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(storageKey(uid, deckId, mode))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return hasValidEnvelope(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function clearSessionSnapshot(
  uid: string,
  deckId: string,
  mode: StudyMode,
  storage = getStorage(),
): void {
  if (!storage) return
  storage.removeItem(storageKey(uid, deckId, mode))
}
